import {Component, ViewEncapsulation, Input, OnInit, OnChanges, Output, EventEmitter, ElementRef, AfterViewInit, OnDestroy} from '@angular/core';
import { TranslationService } from '../../providers/translation.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'tidy-text',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./text.component.scss'],
    template: `
    @if (loaded) {
      <span class="divider-line">
        <span class="divider-line__left"></span>
        <span class="divider-line__center"></span>
        <span class="divider-line__right"></span>
      </span>

      @if (action.observers.length > 0) {
        @if (rippleEffect && !clickableBody && !clickableHeader && !smallHelper) {
          <a
            mat-button
            class="link action-body-inline"
            href="#"
            tappable
            [class.extra-padding]="currentText?.length < 30"
            (click)="executeAction($event)">
            {{currentText | parseText:checkLineBreaks:linkify}}
          </a>
        }
        @if (!rippleEffect && !clickableBody && !clickableHeader && !smallHelper) {
          <span
            class="body-text link"
            [innerHTML]="currentText | parseText:checkLineBreaks:linkify"
            (click)="executeAction($event)"
            class="clickable-body-inline">
          </span>
        }
        @if (clickableBody) {
          <span
            [innerHTML]="currentText | parseText:checkLineBreaks:linkify"
            (click)="executeAction($event)"
            class="clickable-body-inline">
          </span>
        }
        @if (clickableHeader) {
          <span
            [innerHTML]="currentText | parseText:checkLineBreaks:linkify"
            (click)="executeAction($event)"
            class="clickable-header-inline">
          </span>
        }
        @if (smallHelper) {
          <span
            [innerHTML]="currentText | parseText:checkLineBreaks:linkify"
            (click)="executeAction($event)"
            class="clickable-small-helper-inline">
          </span>
        }
      }

      @if (!h3) {
        <span>
          <ng-container *ngTemplateOutlet="textTemplateRef">
          </ng-container>
        </span>
      }
      @if (h3) {
        <h3>
          <ng-container *ngTemplateOutlet="textTemplateRef">
          </ng-container>
        </h3>
      }

      <ng-template #textTemplateRef>
        @if (action.observers.length === 0) {
          <span
            class="body-text"
            [innerHTML]="currentText | parseText:checkLineBreaks:linkify"
            [className]="'body-text'"
            [ngClass]="cssClass"
            >
          </span>
        }
      </ng-template>
    }
    `,
    standalone: false
})
export class TextComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  
  @Input() actionBody: string;
  @Input() body: string;
  @Input() fieldHelperText: string;
  @Input() fieldLabel: string;
  @Input() secondaryBody: string;
  @Input() smallSecondaryBody: string;
  @Input() smallHelper: string;
  @Input() helper: string;
  @Input() header: string;
  @Input() title: string;
  @Input() largeTitle: string;
  @Input() text: string;
  @Input() largeBody: string;
  @Input() mediumBody: string;
  @Input() smallBody: string;
  @Input() h3: string;
  @Input() rippleEffect = true;
  @Input() translate = true;
  @Input() checkLineBreaks = false;
  @Input() linkify = false;
  @Input() headerTitle: string;
  @Input() headerButton: string;
  @Input() clickableBody: string;
  @Input() clickableHeader: string;
  @Input() heroBody: string;
  @Input() megaHero: string;
  @Input() navTitle: string;

  cooldown = false;
  loaded: boolean;

  @Output() action: EventEmitter<any> = new EventEmitter();

  languageChangeSubscription: Subscription;

  constructor(
    private elementRef: ElementRef,
    private translationService: TranslationService
  ) {}

  currentText = '';
  items = {
    actionBody: 'action-body-inline',
    body: 'body-inline',
    fieldHelperText: 'field-helper-text-inline',
    fieldLabel: 'field-label-inline',
    header: 'header-inline',
    largeBody: 'large-body-inline',
    mediumBody: 'medium-body-inline',
    secondaryBody: 'secondary-body-inline',
    smallBody: 'small-body-inline',
    smallSecondaryBody: 'small-secondary-body-inline',
    title: 'title-inline',
    largeTitle: 'large-title-inline',
    helper: 'helper-inline',
    smallHelper: 'small-helper-inline',
    h3: 'h3-inline',
    headerTitle: 'header-title-inline',
    headerButton: 'header-button-inline',
    clickableBody: 'clickable-body-inline',
    clickableHeader: 'clickable-header-inline',
    heroBody: 'hero-body-inline',
    megaHero: 'mega-hero-inline',
    navTitle: 'nav-title-inline'
  };
  cssClass: { [key: string]: boolean };

  executeAction(e: Event) {
    e.preventDefault();
    e.stopPropagation();

    if (this.cooldown) {return;}
    this.cooldown = true;
    setTimeout(() => this.cooldown = false, 500);
    this.action.emit();
  }

  async ngOnInit() {
    await this.setContent();
    const shouldListenToLanguageChange = localStorage.getItem('shouldListenToLanguageChange');
    if (shouldListenToLanguageChange === 'true') {
      this.languageChangeSubscription = this.translationService.languageChange.subscribe(() => {
        this.setContent();
      });
    }
  }

  async ngOnChanges() {
    await this.setContent();
  }

  ngAfterViewInit() {
    const htmlElementRef = this.elementRef.nativeElement as HTMLElement;

    const bodyText = htmlElementRef.querySelector('.body-text') as HTMLElement;
    const centerDivider = htmlElementRef.querySelector('.divider-line__center') as HTMLElement;

    if (!(bodyText && centerDivider)) { return; }

    const widthValue = `${bodyText.offsetWidth + 10}px`;
    centerDivider.style.minWidth = widthValue;
    centerDivider.style.width = widthValue;
  }

  async setContent() {
    try {
      this.loaded = false;
      const activeSourceFound = Object.keys(this).filter(i => this.items[i.toString()]);
      if (activeSourceFound.length === 1) {
        const activeSource = activeSourceFound[0];
        this.cssClass =  {[this.items[activeSource]]: true};
        this.currentText = this[activeSource];
        const language = localStorage.getItem('language');
        if (language && language !== 'en') {
          if (this.translationService.translations[this.currentText]) {
            this.currentText =  this.translationService.translations[this.currentText][language];
          }
        }
      }
    } catch(_err) {
      // Silent error handling - translation failure should not break the component
    }
    this.loaded = true;
  }

  ngOnDestroy() {
    if (this.languageChangeSubscription) {
      this.languageChangeSubscription.unsubscribe();
    }
  }

}
